/**
 * The OAuth pending marker (design section 2). An OAuth return and a sign-in link return look the same
 * in the URL (same landing paths, same `?code=`, and `access_denied` sits beside `otp_expired` too), so
 * before sending the person to Google or Apple the portal writes down, in this tab, that it did and why.
 * `sessionStorage`: one tab, and it survives the round trip to the provider.
 *
 * Every storage access is guarded: a browser that refuses storage simply has no marker, and the return
 * reads as a link return. `from` is where to go after signing in; it is only ever a same-origin app path.
 */
export type OAuthProvider = 'google' | 'apple';

export interface OAuthPending {
  purpose: 'signin' | 'link';
  provider: OAuthProvider;
  /** The landing path the provider returns to; a marker counts only there. */
  path: string;
  from: string | null;
  at: number;
}

export const OAUTH_PENDING_KEY = 'coreway.oauth';
/** A round trip to Google or Apple longer than this is abandoned: its marker no longer counts. */
export const OAUTH_PENDING_TTL_MS = 10 * 60_000;

function sessionStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** A same-origin app path or null: must start with '/', not '//', no scheme, no backslash. */
export function safeAppPath(p: unknown): string | null {
  if (typeof p !== 'string' || !p.startsWith('/') || p.startsWith('//') || p.includes('\\')) return null;
  if (/^[a-z][a-z0-9+.-]*:/i.test(p)) return null;
  return p;
}

export function writeOAuthPending(p: Omit<OAuthPending, 'at'>, storage: Storage | null = sessionStore(), now: number = Date.now()): void {
  const marker: OAuthPending = { purpose: p.purpose, provider: p.provider, path: p.path, from: safeAppPath(p.from), at: now };
  try {
    storage?.setItem(OAUTH_PENDING_KEY, JSON.stringify(marker));
  } catch {
    // No storage: the return reads as a sign-in link return.
  }
}

/** Forget the marker (a refused start leaves nothing behind). */
export function clearOAuthPending(storage: Storage | null = sessionStore()): void {
  try {
    storage?.removeItem(OAUTH_PENDING_KEY);
  } catch {
    // Nothing to forget.
  }
}

function parse(raw: string | null): OAuthPending | null {
  if (!raw) return null;
  let v: Partial<OAuthPending>;
  try {
    v = JSON.parse(raw) as Partial<OAuthPending>;
  } catch {
    return null;
  }
  if (!v || typeof v !== 'object') return null;
  if (v.purpose !== 'signin' && v.purpose !== 'link') return null;
  if (v.provider !== 'google' && v.provider !== 'apple') return null;
  if (typeof v.path !== 'string' || typeof v.at !== 'number' || !Number.isFinite(v.at)) return null;
  return { purpose: v.purpose, provider: v.provider, path: v.path, from: safeAppPath(v.from), at: v.at };
}

/** Reads and REMOVES the marker; null when absent, malformed, older than the TTL, or for another path. */
export function takeOAuthPending(pathname: string, storage: Storage | null = sessionStore(), now: number = Date.now()): OAuthPending | null {
  let raw: string | null = null;
  try {
    raw = storage?.getItem(OAUTH_PENDING_KEY) ?? null;
    storage?.removeItem(OAUTH_PENDING_KEY);
  } catch {
    return null;
  }
  const marker = parse(raw);
  if (!marker || marker.path !== pathname) return null;
  if (now - marker.at > OAUTH_PENDING_TTL_MS || now < marker.at) return null;
  return marker;
}
